/**
 * i18n Context
 * Provides translation function and language switching.
 */
import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { TranslationKey, TranslationDict } from './en';
import en from './en';
import { LANGUAGES, type LanguageCode } from './languages';

interface I18nContextValue {
  /** Current language code */
  locale: LanguageCode;
  /** Set the active language */
  setLocale: (code: LanguageCode) => void;
  /** Translate a key, with optional interpolation variables */
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  /** List of available languages */
  languages: typeof LANGUAGES;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// Lazy-load translation modules (only English is bundled eagerly)
const loaders: Record<LanguageCode, () => Promise<{ default: TranslationDict }>> = {
  en: () => Promise.resolve({ default: en }),
  es: () => import('./translations/es'),
  fr: () => import('./translations/fr'),
  de: () => import('./translations/de'),
  pt: () => import('./translations/pt'),
  it: () => import('./translations/it'),
  nl: () => import('./translations/nl'),
  ru: () => import('./translations/ru'),
  zh: () => import('./translations/zh'),
  ja: () => import('./translations/ja'),
  ko: () => import('./translations/ko'),
  ar: () => import('./translations/ar'),
  hi: () => import('./translations/hi'),
  tr: () => import('./translations/tr'),
  pl: () => import('./translations/pl'),
  vi: () => import('./translations/vi'),
  th: () => import('./translations/th'),
  id: () => import('./translations/id'),
  uk: () => import('./translations/uk'),
  cs: () => import('./translations/cs'),
  bg: () => import('./translations/bg'),
  hr: () => import('./translations/hr'),
  da: () => import('./translations/da'),
  et: () => import('./translations/et'),
  fi: () => import('./translations/fi'),
  el: () => import('./translations/el'),
  hu: () => import('./translations/hu'),
  ga: () => import('./translations/ga'),
  lv: () => import('./translations/lv'),
  lt: () => import('./translations/lt'),
  mt: () => import('./translations/mt'),
  ro: () => import('./translations/ro'),
  sk: () => import('./translations/sk'),
  sl: () => import('./translations/sl'),
  sv: () => import('./translations/sv'),
  ur: () => import('./translations/ur'),
  fa: () => import('./translations/fa'),
  bn: () => import('./translations/bn'),
  ta: () => import('./translations/ta'),
  te: () => import('./translations/te'),
  mr: () => import('./translations/mr'),
  gu: () => import('./translations/gu'),
  kn: () => import('./translations/kn'),
  ml: () => import('./translations/ml'),
};

// Cache loaded dictionaries
const cache: Partial<Record<LanguageCode, TranslationDict>> = { en };

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const val = vars[key];
    return val !== undefined ? String(val) : `{${key}}`;
  });
}

export function I18nProvider({
  defaultLocale = 'en',
  children,
}: {
  defaultLocale?: LanguageCode;
  children: React.ReactNode;
}) {
  const [locale, setLocaleState] = useState<LanguageCode>(defaultLocale);
  const [dict, setDict] = useState<TranslationDict>(cache[defaultLocale] || en);

  // Sync when defaultLocale prop changes after mount
  useEffect(() => {
    setLocaleState(defaultLocale);
    if (cache[defaultLocale]) {
      setDict(cache[defaultLocale]);
    } else {
      const loader = loaders[defaultLocale];
      if (loader) {
        loader().then((mod) => {
          cache[defaultLocale] = mod.default;
          setDict(mod.default);
        });
      }
    }
  }, [defaultLocale]);

  const setLocale = useCallback((code: LanguageCode) => {
    setLocaleState(code);
    if (cache[code]) {
      setDict(cache[code]);
      return;
    }
    const loader = loaders[code];
    if (loader) {
      loader().then((mod) => {
        cache[code] = mod.default;
        setDict(mod.default);
      });
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      const template = dict[key] || en[key] || key;
      return interpolate(template, vars);
    },
    [dict],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, languages: LANGUAGES }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Hook to access translation function and locale controls */
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback for components used outside provider — returns English
    return {
      locale: 'en' as LanguageCode,
      setLocale: () => {},
      t: (key: TranslationKey, vars?: Record<string, string | number>) =>
        interpolate(en[key] || key, vars),
      languages: LANGUAGES,
    };
  }
  return ctx;
}

/** Shorthand hook — returns just the `t` function */
export function useT() {
  return useI18n().t;
}
